import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider, Header, Footer, MobileBottomCTA, ScrollToTop, DocumentHead } from "@/kindra";
import ComponentsShowcase from "@/pages/ComponentsShowcase";
import IconsQC from "@/pages/IconsQC";
import LeakCheckPage from "@/pages/LeakCheckPage";
import AdminTaxCalculatorPage from "@/pages/AdminTaxCalculatorPage";
// ClarityPage (old AppLayout bucket design) archived 2026-02-26 — route
// remapped to V2ClarityQRPage so the QR codes on Michael's van serve the
// current V2 brand. File preserved at /pages/ClarityPage.jsx for reference.
import {
  ServicesPage, ServiceDetailPage, ResultsPage,
  ContactPage, GuidePage, ThankYouGuidePage,
  NotFoundPage, PrivacyPage, TermsPage,
} from "@/pages/MarketingPages";
import { AdminLoginPage, AdminChangePasswordPage, AdminDashboardPage } from "@/pages/AdminPages";
// V2 — promoted from staging on 2026-06-24. These now serve the bare-domain
// main nav (/, /what-we-fix, /what-we-build, /ai, /case-studies,
// /how-it-works, /about, /book) AND remain reachable at their original
// /v2/* paths as aliases for backwards-compat.
import V2HomePage              from "@/v2/V2HomePage";
import V2WhatWeFixPage         from "@/v2/V2WhatWeFixPage";
import V2WhatWeBuildPage       from "@/v2/V2WhatWeBuildPage";
import V2BuiltPage             from "@/v2/V2BuiltPage";
import V2AIPage                from "@/v2/V2AIPage";
import V2CaseStudiesPage       from "@/v2/V2CaseStudiesPage";
import V2HowItWorksPage        from "@/v2/V2HowItWorksPage";
import V2AboutPage             from "@/v2/V2AboutPage";
import V2ClarityQRPage         from "@/v2/V2ClarityQRPage";
import V2FromMichaelPage       from "@/v2/V2FromMichaelPage";
import V2SampleReportPage      from "@/v2/V2SampleReportPage";
import V2LeakTeaserPage        from "@/v2/V2LeakTeaserPage";
import V2AdminLeadsPage        from "@/v2/V2AdminLeadsPage";
import V2AdminVaultPage        from "@/v2/V2AdminVaultPage";
import {
  V2InsightsIndexPage,
  V2InsightsArticlePage,
} from "@/v2/V2InsightsPreviewPage";
import {
  V2ThinkingIndexPage,
  V2ThinkingArticlePage,
} from "@/v2/V2ThinkingPage";
import V2WhatWeDoPage          from "@/v2/V2WhatWeDoPage";
import V2AdminOutreachPage     from "@/v2/V2AdminOutreachPage";
import V2DeskPage              from "@/v2/V2DeskPage";
import V2ConsultingQuotesPage  from "@/v2/V2ConsultingQuotesPage";
import V2ProjectsPage         from "@/v2/V2ProjectsPage";
import V2CQPublicPage          from "@/v2/V2CQPublicPage";
import V2PitchPage             from "@/v2/V2PitchPage";
import V2ClientAreaPage        from "@/v2/V2ClientAreaPage";
import V2AdminDemosPage        from "@/v2/V2AdminDemosPage";
import V2AdminBlueprintPage    from "@/v2/V2AdminBlueprintPage";
import V2AdminHelpPage        from "@/v2/V2AdminHelpPage";
import V2AdminConnectorsPage  from "@/v2/V2AdminConnectorsPage";
import V2AdminWorkshopPage, { V2AdminWorkshopSessionPage } from "@/v2/V2AdminWorkshopPage";
import V2WorkshopClientPage    from "@/v2/V2WorkshopClientPage";
import V2DemoPage              from "@/v2/V2DemoPage";
import V2BookingPage           from "@/v2/V2BookingPage";
import V2ThankYouBookingPage   from "@/v2/V2ThankYouBookingPage";
import V2DiscoverPage          from "@/v2/V2DiscoverPage";
import V2AdminOSPage, { V2AdminOSDetailPage } from "@/v2/V2AdminOSPage";
import { V2AdminDeliveryListPage, V2AdminDeliveryDetailPage } from "@/v2/V2AdminDeliveryPage";
import V2QuoteBuilderPage, { V2ClientQuotePage } from "@/v2/V2QuotePages";
import { V2Layout }            from "@/v2/V2Layout";

function AppLayout({ children, footerHidden = false, hideMobileCta = false }) {
  // Wave 1 unification (2026-07-03): AppLayout now delegates to V2Layout so
  // every legacy page (leak-check, contact, guide, privacy, terms, thank-you/guide,
  // 404, and the archived services/results/etc.) renders with the SAME header
  // and footer as the live marketing site. Kills the two-nav split noted in the
  // four-lens audit. V2Layout doesn't have a mobile bottom CTA overlay so the
  // `hideMobileCta` prop is naturally moot; `footerHidden` maps to `minimalFooter`.
  void hideMobileCta;
  return (
    <V2Layout minimalFooter={footerHidden}>
      {children}
    </V2Layout>
  );
}

// Client-side belt-and-braces redirect for the legacy article URLs
// (/insights-preview/:slug → /insights/:slug). Nginx sends a 301 in front
// of these, but keeping the SPA route ensures the swap is clean even if
// somebody bookmarked a preview URL that already has an SPA-only path.
function InsightsPreviewRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/insights/${slug}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ScrollToTop />
        <DocumentHead />
        <Routes>
          {/* ────────────────────────────────────────────────────────────
              LIVE — promoted from V2 staging on 2026-06-24.
              The 8 V2 pages now serve the bare-domain main nav: home, what
              we fix, what we build, AI, case studies, how it works, about,
              and book. They use V2Layout (their own header/footer chrome)
              rather than AppLayout. Legacy /pages/* components remain
              wired to their original routes below for backwards-compat
              with any external links / search results pointing at them. */}
          {/* TravelTrucks OS is an internal cockpit — the root opens straight
              to the board; the Kindra public marketing site is unrouted (its
              routes below fall through to the catch-all redirect). */}
          <Route path="/"                element={<Navigate to="/admin" replace />} />
          {/* Kindra marketing routes removed — all redirect to the OS via
              the catch-all. Booking/legal belong to the deferred TravelTrucks
              marketing rebuild. */}

          {/* (from-michael, sample-report, leak teaser, insights, thinking,
              discover, services, clarity, guide, results, privacy, terms,
              QC/showcase, and all /v2/* staging routes removed — Kindra
              marketing; they redirect to the OS via the catch-all.) */}

          {/* Admin (no shared layout — own chrome) */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/change-password" element={<AdminChangePasswordPage />} />
          {/* /admin → the TravelTrucks OS cockpit (Base Camp board). */}
          <Route path="/admin" element={<V2DeskPage />} />
          <Route path="/admin/desk" element={<V2DeskPage />} />
          <Route path="/admin/quotes" element={<V2ConsultingQuotesPage />} />
          <Route path="/admin/projects" element={<V2ProjectsPage />} />
          <Route path="/q/:token" element={<V2CQPublicPage />} />
          <Route path="/admin/clients" element={<V2ClientAreaPage />} />
          <Route path="/admin/clients/:itemId" element={<V2ClientAreaPage />} />
          {/* Admin → Connectors — plug in Xero/Fieldy/MS365/GHL/WorkflowMax/Stripe. */}
          <Route path="/admin/connectors" element={<V2AdminConnectorsPage />} />
          {/* Agency admin removed from Col's view (blueprint / outreach /
              demos / workshop / leads / delivery / review-queue / vault /
              legacy). Kept: the quote builder + the How-this-works page.
              Everything unrouted here redirects via the catch-all below. */}
          <Route path="/admin/os/quotes/:quoteId" element={<V2QuoteBuilderPage />} />
          <Route path="/admin/os/help" element={<V2AdminHelpPage />} />

          {/* Public client-facing quote — token-only, no auth, no chrome. */}
          <Route path="/quote/:token" element={<V2ClientQuotePage />} />

          {/* Unknown / unrouted (incl. legacy Kindra marketing + demo tokens)
              → the OS. Nothing Kindra-branded is reachable in Col's view. */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
